import { useState } from 'react'
import { styled } from 'styled-components'
import { IoClose } from 'react-icons/io5'

interface Props {
    currentName: string
    onClose: () => void
    onConfirm: (newName: string) => void
}

const Overlay = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Modal = styled.div`
    background: white;
    border-radius: 12px;
    max-width: 450px;
    width: 90%;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid #f0f0f0;
`

const Title = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: #000000;
    margin: 0;
`

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999999;
    transition: color 0.2s;

    &:hover {
        color: #333333;
    }

    svg {
        width: 24px;
        height: 24px;
    }
`

const Content = styled.div`
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const Label = styled.label`
    font-size: 14px;
    font-weight: 500;
    color: #666666;
`

const Input = styled.input`
    padding: 12px 16px;
    border: 1px solid #1fa474;
    border-radius: 6px;
    font-size: 14px;
    background-color: #ffffff;
    color: #333333;
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: #1fa474;
        box-shadow: 0 0 0 2px rgba(31, 164, 116, 0.1);
    }

    &::placeholder {
        color: #999999;
    }
`

const Footer = styled.div`
    padding: 24px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
`

const Button = styled.button`
    padding: 12px 32px;
    border: none;
    border-radius: 24px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`

const CancelButton = styled(Button)`
    background-color: #f0f0f0;
    color: #333333;

    &:hover:not(:disabled) {
        background-color: #e0e0e0;
    }
`

const ConfirmButton = styled(Button)`
    background-color: #1fa474;
    color: white;

    &:hover:not(:disabled) {
        background-color: #168a5d;
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }
`

const ErrorMessage = styled.div`
    padding: 12px 16px;
    background-color: #fee;
    border: 1px solid #fcc;
    border-radius: 6px;
    color: #c33;
    font-size: 14px;
`

export default function RenameClassModal({
    currentName,
    onClose,
    onConfirm,
}: Props) {
    const [newName, setNewName] = useState(currentName)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)

    const handleConfirm = async () => {
        const trimmedName = newName.trim()

        if (!trimmedName) {
            setError('Class name is required')
            return
        }

        if (trimmedName === currentName) {
            setError('New name must be different from current name')
            return
        }

        setLoading(true)
        setError(null)

        try {
            await onConfirm(trimmedName)
            onClose()
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : 'Failed to rename class'
            setError(errorMessage)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <Header>
                    <Title>Rename Class</Title>
                    <CloseButton onClick={onClose}>
                        <IoClose />
                    </CloseButton>
                </Header>

                <Content>
                    <FormGroup>
                        <Label>Class Name</Label>
                        <Input
                            type="text"
                            value={newName}
                            onChange={(e) => {
                                setNewName(e.target.value)
                                setError(null)
                            }}
                            placeholder="Enter new class name"
                            autoFocus
                        />
                    </FormGroup>

                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </Content>

                <Footer>
                    <CancelButton onClick={onClose} disabled={loading}>
                        Cancel
                    </CancelButton>
                    <ConfirmButton onClick={handleConfirm} disabled={loading}>
                        {loading ? 'Renaming...' : 'Rename'}
                    </ConfirmButton>
                </Footer>
            </Modal>
        </Overlay>
    )
}
